import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase';
import Header from './Header';
import './Manager.css';

function AwarenessImagesManager() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [formData, setFormData] = useState({
    imageUrl: '',
    caption: '',
    order: 1,
  });

  useEffect(() => {
    loadImages();
  }, []);

  const loadImages = async () => {
    try {
      const q = query(collection(db, 'awarenessImages'), orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      const imagesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setImages(imagesData);
    } catch (error) {
      console.error('Error loading awareness images:', error);
      alert('Failed to load awareness images');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingImage) {
        await updateDoc(doc(db, 'awarenessImages', editingImage.id), {
          ...formData,
          updatedAt: new Date().toISOString(),
        });
        alert('Awareness image updated successfully!');
      } else {
        await addDoc(collection(db, 'awarenessImages'), {
          ...formData,
          createdAt: new Date().toISOString(),
        });
        alert('Awareness image added successfully!');
      }
      setShowModal(false);
      setEditingImage(null);
      resetForm();
      loadImages();
    } catch (error) {
      console.error('Error saving awareness image:', error);
      alert('Failed to save awareness image');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (image) => {
    setEditingImage(image);
    setFormData({
      imageUrl: image.imageUrl,
      caption: image.caption,
      order: image.order,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this awareness image?')) return;

    try {
      await deleteDoc(doc(db, 'awarenessImages', id));
      alert('Awareness image deleted successfully!');
      loadImages();
    } catch (error) {
      console.error('Error deleting awareness image:', error);
      alert('Failed to delete awareness image');
    }
  };

  const resetForm = () => {
    setFormData({
      imageUrl: '',
      caption: '',
      order: 1,
    });
  };

  return (
    <div>
      <Header />

      <div className="container">
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Awareness Images Management</h2>
            <button
              onClick={() => {
                setEditingImage(null);
                resetForm();
                setShowModal(true);
              }}
              className="btn btn-primary"
            >
              + Add New Image
            </button>
          </div>

          {loading && <p>Loading...</p>}

          {!loading && images.length === 0 && (
            <p>No awareness images found. Add your first image!</p>
          )}

          {!loading && images.length > 0 && (
            <table className="table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Image Preview</th>
                  <th>Image URL</th>
                  <th>Caption</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {images.map((image) => (
                  <tr key={image.id}>
                    <td>{image.order}</td>
                    <td>
                      <img
                        src={image.imageUrl}
                        alt={image.caption}
                        style={{ width: '100px', height: '75px', objectFit: 'cover', borderRadius: '4px' }}
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/100x75?text=No+Image'; }}
                      />
                    </td>
                    <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {image.imageUrl}
                    </td>
                    <td>{image.caption}</td>
                    <td className="actions">
                      <button
                        onClick={() => handleEdit(image)}
                        className="btn btn-secondary"
                        style={{ marginRight: '8px' }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(image.id)}
                        className="btn btn-danger"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="info-box">
          <h3>Awareness Images Guidelines</h3>
          <p>• Use direct image URLs from reliable sources</p>
          <p>• Recommended size: 1200x675px (16:9 aspect ratio) or higher</p>
          <p>• Supported formats: JPG, PNG, WebP</p>
          <p>• Images should be awareness posters related to Beti Bachao Beti Padhao</p>
          <p>• Add clear, concise captions describing the message</p>
        </div>
      </div>

      {showModal && (
        <div className="modal" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingImage ? 'Edit Awareness Image' : 'Add New Awareness Image'}
              </h2>
              <button
                className="close-btn"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Image URL *</label>
                <input
                  type="url"
                  value={formData.imageUrl}
                  onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                  placeholder="https://images.unsplash.com/photo-..."
                  required
                />
                {formData.imageUrl && (
                  <div style={{ marginTop: '10px' }}>
                    <img
                      src={formData.imageUrl}
                      alt="Preview"
                      style={{ width: '100%', maxWidth: '500px', borderRadius: '8px' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Caption *</label>
                <input
                  type="text"
                  value={formData.caption}
                  onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
                  placeholder="e.g., Save the Girl Child - Every Girl Matters"
                  required
                />
              </div>

              <div className="form-group">
                <label>Order *</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  min="1"
                  required
                />
              </div>

              <div className="actions">
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : editingImage ? 'Update Image' : 'Add Image'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingImage(null);
                    resetForm();
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AwarenessImagesManager;
